import React, { useEffect, useState, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import DashboardController from '../Controllers/DashboardController';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

export default function ApiUsage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await DashboardController.getApiUsage();
      setUsage(data);
    } catch (err) {
      setError('Failed to load API usage. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const dailyUsage = usage?.dailyUsage ?? [];

  const barChartData = {
    labels: dailyUsage.map((d) => d.label),
    datasets: [
      {
        label: 'Requests',
        data: dailyUsage.map((d) => d.requests),
        backgroundColor: '#2f5fdb',
        borderRadius: 4,
        maxBarThickness: 22,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0, color: '#8a93a7' }, grid: { color: '#f0f2f8' } },
      x: { grid: { display: false }, ticks: { color: '#8a93a7' } },
    },
  };

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">API Usage</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              Request volume for your integration key
            </p>
          </div>

          <button className="ivs-icon-btn" onClick={fetchUsage} disabled={loading} aria-label="Refresh" type="button">
            <i className={`bi ${loading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'}`}></i>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {!loading && usage && (
          <>
            <div className="ivs-stat-grid">
              <div className="ivs-stat-card">
                <div>
                  <div className="ivs-stat-card__label">Today</div>
                  <div className="ivs-stat-card__value">{usage.requestsToday.toLocaleString()}</div>
                  <div className="ivs-stat-card__title">Requests</div>
                </div>
                <div className="ivs-stat-card__icon" style={{ background: 'var(--ivs-primary-light)', color: 'var(--ivs-primary)' }}>
                  <i className="bi bi-lightning-charge"></i>
                </div>
              </div>

              <div className="ivs-stat-card">
                <div>
                  <div className="ivs-stat-card__label">This Month</div>
                  <div className="ivs-stat-card__value">{usage.requestsMonth.toLocaleString()}</div>
                  <div className="ivs-stat-card__title">Requests</div>
                </div>
                <div className="ivs-stat-card__icon" style={{ background: 'var(--ivs-primary-light)', color: 'var(--ivs-primary)' }}>
                  <i className="bi bi-graph-up"></i>
                </div>
              </div>

              <div className="ivs-stat-card">
                <div>
                  <div className="ivs-stat-card__label">{usage.environment ? usage.environment.toUpperCase() : 'No key'}</div>
                  <div className="ivs-stat-card__value" style={{ fontSize: 22 }}>
                    {usage.apikeyMasked || '—'}
                  </div>
                  <div className="ivs-stat-card__title">API Key</div>
                </div>
                <div className="ivs-stat-card__icon" style={{ background: 'var(--ivs-primary-light)', color: 'var(--ivs-primary)' }}>
                  <i className="bi bi-key"></i>
                </div>
              </div>
            </div>

            <div className="ivs-panel">
              <div className="ivs-panel__header">
                <h3 className="ivs-panel__title">Daily Requests · Last 14 Days</h3>
              </div>
              <div style={{ height: 260 }}>
                <Bar data={barChartData} options={barChartOptions} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
